'use client'

/**
 * `@/routing` — the app's routing layer on top of `next/navigation` +
 * `next/link`. Exposes navigation hooks/components (`useNavigate`, `useParams`,
 * `useLocation`, `useSearchParams`, `Link`, `Navigate`, `Outlet`) plus small
 * path helpers (`generatePath`, `createSearchParams`). Under Vitest,
 * `next/navigation` + `next/link` are mocked in `src/vitest.setup.ts`.
 *
 * Caveat: Next holds no router navigation state, so `location.state` is always
 * `null` and `navigate(to, { state })` / `<Navigate state>` drop `state`.
 */

import NextLink from 'next/link'
import {
  useParams as useNextParams,
  usePathname,
  useRouter,
  useSearchParams as useNextSearchParams,
} from 'next/navigation'
import {
  type AnchorHTMLAttributes,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { prefetchView } from './viewPrefetch'

// --- Types ----------------------------------------------------------------

export type To =
  | string
  | Partial<{ pathname: string; search: string; hash: string }>

export interface Location<S = unknown> {
  pathname: string
  search: string
  hash: string
  state: S
  key: string
}

export type Params<Key extends string = string> = {
  readonly [key in Key]: string | undefined
}

export interface NavigateOptions {
  replace?: boolean
  state?: unknown
  preventScrollReset?: boolean
}

export interface NavigateFunction {
  (to: To, options?: NavigateOptions): void
  (delta: number): void
}

export type SetURLSearchParams = (
  nextInit?:
    | URLSearchParams
    | Record<string, string>
    | string
    | ((
        prev: URLSearchParams
      ) => URLSearchParams | Record<string, string> | string),
  navigateOpts?: { replace?: boolean; state?: unknown }
) => void

/** Client-loader args: matches the `{ params }` destructured by guards/loaders. */
export interface LoaderFunctionArgs {
  request: Request
  params: Params
  context?: unknown
}

// --- Path helpers ---------------------------------------------------------

/**
 * Build a concrete path from a pattern with `:param`, optional `:param?` and a
 * trailing `*` splat; values are URL-encoded. Route enums (e.g.
 * `/pooling/:slug/edit/:step`) are written against this.
 */
export function generatePath(
  originalPath: string,
  params: { [key: string]: string | null | undefined } = {}
): string {
  let path = originalPath
  if (path.endsWith('*') && path !== '*' && !path.endsWith('/*')) {
    path = path.replace(/\*$/, '/*')
  }
  const prefix = path.startsWith('/') ? '/' : ''
  const stringify = (p: unknown) =>
    p == null ? '' : typeof p === 'string' ? p : String(p)
  const segments = path
    .split(/\/+/)
    .map((segment, index, array) => {
      const isLastSegment = index === array.length - 1
      if (isLastSegment && segment === '*') {
        return stringify(params['*'])
      }
      const keyMatch = segment.match(/^:([\w-]+)(\??)(.*)/)
      if (keyMatch) {
        const [, key, optional, suffix] = keyMatch
        const param = params[key]
        if (optional !== '?' && param == null) {
          throw new Error(`Missing ":${key}" param for path "${originalPath}"`)
        }
        return encodeURIComponent(stringify(param)) + suffix
      }
      return segment.replace(/\?$/g, '')
    })
    .filter((segment) => !!segment)
  return prefix + segments.join('/')
}

/** Build a `URLSearchParams` from a record/string/array of pairs. */
export function createSearchParams(
  init:
    | string
    | URLSearchParams
    | string[][]
    | Record<string, string | string[]> = ''
): URLSearchParams {
  if (
    typeof init === 'string' ||
    Array.isArray(init) ||
    init instanceof URLSearchParams
  ) {
    return new URLSearchParams(init)
  }
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, v))
    else params.append(key, value)
  }
  return params
}

export type RedirectSignal = { readonly __redirect: true; readonly to: string }

/**
 * Signal a redirect, thrown from a client resolver/grant check; the contextual
 * guard catches it and navigates (this client-side flow has no server redirect).
 */
export function redirect(to: string): RedirectSignal {
  return { __redirect: true, to }
}

// --- Navigation hooks & components ----------------------------------------

const hrefFromTo = (to: To | string): string => {
  if (typeof to === 'string') return to
  return `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`
}

export function useNavigate(): NavigateFunction {
  const router = useRouter()
  return useCallback(
    (to: To | string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        if (to < 0) router.back()
        else if (to > 0) router.forward()
        return
      }
      const href = hrefFromTo(to)
      if (options?.replace) router.replace(href)
      else router.push(href)
    },
    [router]
  ) as NavigateFunction
}

// react-router decoded params; malformed sequences fall back to the raw value.
const decodeParam = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function useParams<
  T extends Record<string, string | undefined> = Params,
>(): T {
  // next/navigation params can be string | string[] and are percent-encoded;
  // the app only uses single segments, so collapse arrays to their first
  // value and decode them (react-router semantics, which callers rely on for
  // IRI params like `/admin/subscriptions/:id`).
  const params = useNextParams()
  return useMemo(() => {
    const out: Record<string, string | undefined> = {}
    for (const [key, value] of Object.entries(params ?? {})) {
      const single = Array.isArray(value) ? value[0] : value
      out[key] = single == null ? single : decodeParam(single)
    }
    return out as T
  }, [params])
}

export function useLocation(): Location {
  const pathname = usePathname()
  const searchParams = useNextSearchParams()
  const search = searchParams?.toString() ? `?${searchParams.toString()}` : ''
  return useMemo(
    () => ({
      pathname: pathname ?? '',
      search,
      hash: typeof window !== 'undefined' ? window.location.hash : '',
      state: null,
      key: 'default',
    }),
    [pathname, search]
  )
}

export function useSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const searchParams = useNextSearchParams()
  const pathname = usePathname()
  const router = useRouter()

  const current = useMemo(
    () => new URLSearchParams(searchParams?.toString() ?? ''),
    [searchParams]
  )

  const setSearchParams = useCallback<SetURLSearchParams>(
    (nextInit, navigateOpts) => {
      const resolved =
        typeof nextInit === 'function'
          ? nextInit(new URLSearchParams(current))
          : nextInit
      const params =
        resolved instanceof URLSearchParams
          ? resolved
          : new URLSearchParams(resolved as Record<string, string>)
      const qs = params.toString()
      const url = qs ? `${pathname}?${qs}` : (pathname ?? '')
      if (navigateOpts?.replace) router.replace(url)
      else router.push(url)
    },
    [current, pathname, router]
  )

  return [current, setSearchParams]
}

interface NavigateProps {
  to: To | string
  replace?: boolean
  state?: unknown
}

export function Navigate({ to, replace }: NavigateProps): null {
  const navigate = useNavigate()
  useEffect(() => {
    navigate(to, { replace })
  }, [navigate, to, replace])
  return null
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: To | string
  replace?: boolean
  state?: unknown
  reloadDocument?: boolean
  preventScrollReset?: boolean
  prefetch?: boolean
  children?: ReactNode
}

export const Link = forwardRef(function Link(
  {
    to,
    replace,
    state: _state,
    reloadDocument,
    preventScrollReset,
    prefetch,
    children,
    onMouseEnter,
    onFocus,
    ...rest
  }: LinkProps,
  ref: ForwardedRef<HTMLAnchorElement>
) {
  const href = hrefFromTo(to)

  // Warm the target's lazy view chunk on navigation intent (hover/focus), then
  // run any caller-provided handler. next/link prefetches the route payload;
  // this adds the view chunk that clientPage loads lazily (see ./viewPrefetch).
  const handleMouseEnter = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      prefetchView(href)
      onMouseEnter?.(e)
    },
    [href, onMouseEnter]
  )
  const handleFocus = useCallback(
    (e: FocusEvent<HTMLAnchorElement>) => {
      prefetchView(href)
      onFocus?.(e)
    },
    [href, onFocus]
  )

  if (reloadDocument) {
    return (
      <a
        ref={ref}
        href={href}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        {...rest}
      >
        {children}
      </a>
    )
  }
  return (
    <NextLink
      ref={ref}
      href={href}
      replace={replace}
      scroll={preventScrollReset ? false : undefined}
      prefetch={prefetch}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      {...rest}
    >
      {children}
    </NextLink>
  )
})

// Bridge <Outlet /> to App Router `children` (see ./outlet).
export { OutletProvider } from './outlet'
export { NextOutlet as Outlet } from './outlet'
// Route loader-data context (see ./loaderData) — guards/boundaries provide it.
export { RouteLoaderDataProvider, useRouteLoaderData } from './loaderData'
